import React, { useState, useEffect } from 'react';
import { PencilIcon, TrashIcon, PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import TownshipDeliveryModal from '../../components/admin/TownshipDeliveryModal';
import ConfirmModal from '../../components/admin/ConfirmModal';

interface TownshipDeliveryPrice {
  id: number;
  township_name: string;
  township_name_mm?: string;
  state_region: string;
  state_region_mm?: string;
  delivery_price: number;
  estimated_days: number;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}


const AdminTownshipDelivery: React.FC = () => {
  const [townships, setTownships] = useState<TownshipDeliveryPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedTownship, setSelectedTownship] = useState<TownshipDeliveryPrice | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stateRegionFilter, setStateRegionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stateRegions, setStateRegions] = useState<{value: string, label: string}[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 10;

  useEffect(() => {
    fetchTownships();
    fetchStateRegions();
  }, [searchTerm, stateRegionFilter, statusFilter, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTownships = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: currentPage.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(stateRegionFilter && { state_region: stateRegionFilter }),
        ...(statusFilter && { is_active: statusFilter })
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://139.59.98.59/api'}/admin/township-delivery-prices?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTownships(data.data.data);
        setCurrentPage(data.data.current_page);
        setTotalPages(data.data.last_page);
      }
    } catch (error) {
      console.error('Error fetching townships:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStateRegions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://139.59.98.59/api'}/admin/township-delivery-prices/state-regions/list`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStateRegions(data.data);
      }
    } catch (error) {
      console.error('Error fetching state regions:', error);
    }
  };

  const handleCreate = () => {
    setSelectedTownship(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEdit = (township: TownshipDeliveryPrice) => {
    setSelectedTownship(township);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleView = (township: TownshipDeliveryPrice) => {
    setSelectedTownship(township);
    setModalMode('view');
    setIsModalOpen(true);
  };

  const handleDeleteTownship = (township: TownshipDeliveryPrice) => {
    setConfirmMessage(`Are you sure you want to delete "${township.township_name}" delivery price? This action cannot be undone.`);
    setConfirmAction(() => () => deleteTownship(township.id));
    setShowConfirmModal(true);
  };

  const deleteTownship = async (townshipId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://139.59.98.59/api'}/admin/township-delivery-prices/${townshipId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        fetchTownships();
      }
    } catch (error) {
      console.error('Error deleting township:', error);
    }
  };

  const handleSave = () => {
    fetchTownships();
    setIsModalOpen(false);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US').format(price) + ' Ks';
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive 
          ? 'bg-green-100 text-green-800' 
          : 'bg-red-100 text-red-800'
      }`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);
    
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Township Delivery Prices</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage delivery charges for different townships and regions
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Township
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search townships..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
          />
        </div>

        <select
          value={stateRegionFilter}
          onChange={(e) => setStateRegionFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] cursor-pointer"
        >
          <option value="">All State/Regions</option>
          {stateRegions.map((region) => (
            <option key={region.value} value={region.value}>
              {region.label}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] cursor-pointer"
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>

        <div className="text-sm text-gray-600 flex items-center">
          Total: {townships.length} townships
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Township
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  State/Region
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Delivery Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Est. Days
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : townships.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    No townships found
                  </td>
                </tr>
              ) : (
                townships.map((township) => (
                  <tr key={township.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {township.township_name}
                        </div>
                        {township.township_name_mm && (
                          <div className="text-sm text-gray-500">
                            {township.township_name_mm}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900">
                          {township.state_region}
                        </div>
                        {township.state_region_mm && (
                          <div className="text-sm text-gray-500">
                            {township.state_region_mm}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatPrice(township.delivery_price)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {township.estimated_days} {township.estimated_days === 1 ? 'day' : 'days'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(township.is_active)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleView(township)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="View"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleEdit(township)}
                          className="text-indigo-600 hover:text-indigo-900 p-1"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTownship(township)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            {/* Mobile Pagination */}
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || totalPages <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 active:scale-[0.98]"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages <= 1}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 active:scale-[0.98]"
              >
                Next
              </button>
            </div>
            
            {/* Desktop Pagination */}
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  {townships.length > 0 ? (
                    <>
                      Showing <span className="font-medium">{((currentPage - 1) * perPage) + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(currentPage * perPage, townships.length)}</span> of{' '}
                      <span className="font-medium">{townships.length}</span> results
                    </>
                  ) : (
                    'No results found'
                  )}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  {/* Previous Button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || totalPages <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 active:scale-[0.98]"
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page Numbers - Show even for single page */}
                  {totalPages > 0 && getPageNumbers().map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      disabled={totalPages <= 1}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-all duration-200 ease-in-out hover:shadow-sm focus:outline-none focus:ring-4 focus:ring-gray-100 active:scale-[0.98] ${
                        page === currentPage
                          ? 'z-10 bg-gray-500 border-gray-500 text-white'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 hover:border-gray-400'
                      } ${totalPages <= 1 ? 'cursor-not-allowed opacity-50' : ''}`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  {/* Show page 1 if no pages calculated */}
                  {totalPages === 0 && (
                    <button
                      disabled
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 cursor-not-allowed opacity-50"
                    >
                      1
                    </button>
                  )}
                  
                  {/* Next Button */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || totalPages <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 active:scale-[0.98]"
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      <TownshipDeliveryModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        mode={modalMode}
        township={selectedTownship}
        onSave={handleSave}
      />

      {/* Confirm Modal */}
      {showConfirmModal && (
        <ConfirmModal
          message={confirmMessage}
          onConfirm={() => {
            confirmAction();
            setShowConfirmModal(false);
          }}
          onCancel={() => setShowConfirmModal(false)}
          type="danger"
          confirmText="Delete"
        />
      )}
    </div>
  );
};

export default AdminTownshipDelivery;
